import { Link } from "@tanstack/react-router"
import { Button } from "design-system/atoms/Button"
import { Input } from "design-system/atoms/Input"
import { Label } from "design-system/atoms/Label"
import { CardDescription, CardTitle } from "design-system/molecules/Card"
import { cn } from "design-system/utils"
import { useTranslation } from "react-i18next"
import { LoginSchema } from "schema"
import { z } from "zod"

interface LoginFormProps extends Omit<React.ComponentPropsWithoutRef<"form">, "onSubmit"> {
  onSubmit: (data: z.infer<typeof LoginSchema>) => Promise<void>
  isPending: boolean
  isValid: boolean
}

export function LoginForm({ className, onSubmit, isPending, isValid, ...props }: LoginFormProps) {
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)

    const data = {
      email: formData.get("email") as string,
      password: formData.get("password") as string,
    }

    await onSubmit(data)
  }

  const { t } = useTranslation()

  return (
    <form onSubmit={handleSubmit} className={cn("flex flex-col gap-6", className)} {...props}>
      <div className="flex flex-col gap-4 mb-4 items-center text-xl">
        <CardTitle>{t("auth.login.title")}</CardTitle>
        <CardDescription className="text-foreground">{t("auth.login.description")}</CardDescription>
      </div>
      <div className="grid gap-4">
        <div className="grid gap-2">
          <Label htmlFor="email">{t("auth.email.field")}</Label>
          <Input
            name="email"
            id="email"
            type="email"
            placeholder={t("auth.email.placeholder")}
            required
            className="bg-background/50"
          />
        </div>
        <div className="grid gap-2">
          <div className="flex items-center">
            <Label htmlFor="password">{t("auth.password.field")}</Label>
            <Link
              to="/forgot-password"
              className="ml-auto text-xs underline hover:text-foreground/80"
            >
              {t("auth.login.forgotPassword")}
            </Link>
          </div>
          <Input
            name="password"
            id="password"
            type="password"
            placeholder={t("auth.password.placeholder")}
            required
            className="bg-background/50"
          />
        </div>
        <div className="flex flex-col gap-4 pt-4">
          <Button disabled={isPending || !isValid}>{t("auth.login.submit")}</Button>
        </div>
      </div>
    </form>
  )
}
